import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Shield, Wrench, ChevronDown } from '../lib/icons';
import { getServices } from '../lib/services-store';
import { STABLE_DEVICE_MODELS } from '../data/device-models';
import { DEVICE_CATEGORY_LABELS } from '../types/services';
import type { DeviceCategory, ServiceEntry } from '../types/services';

// ─── Brand → category mapping ─────────────────────────────────────────────────

interface Brand {
  id: string;
  label: string;
  categories: DeviceCategory[];
}

const BRANDS: Brand[] = [
  { id: 'sony',      label: 'Sony',        categories: ['ps5'] },
  { id: 'nintendo',  label: 'Nintendo',    categories: ['switch', 'nintendo-handheld'] },
  { id: 'microsoft', label: 'Microsoft',   categories: ['xbox'] },
  { id: 'handheld',  label: 'Handheld PC', categories: ['handheld-pc'] },
  { id: 'apple',     label: 'Apple',       categories: ['iphone', 'ipad', 'laptop-macbook'] },
  { id: 'samsung',   label: 'Samsung',     categories: ['samsung'] },
  { id: 'other',     label: 'Other',       categories: ['other'] },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const selClass = (disabled: boolean) =>
  `w-full bg-gray-800 border ${disabled ? 'border-gray-800 text-gray-600 cursor-not-allowed' : 'border-gray-700 text-[#E0E0E0] cursor-pointer hover:border-[#39FF14]/50'} rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#39FF14] transition-colors appearance-none`;

const Label = ({ children, active }: { children: React.ReactNode; active: boolean }) => (
  <p className={`text-xs font-semibold uppercase tracking-widest mb-1.5 ${active ? 'text-[#39FF14]' : 'text-gray-600'}`}>
    {children}
  </p>
);

// ─── Main component ───────────────────────────────────────────────────────────

export default function RepairSelector() {
  const navigate = useNavigate();

  const allServices = useMemo(
    () => getServices().filter(s => s.status === 'active' && s.deviceCategory !== 'controller'),
    [],
  );

  // ── Cascading selection state ─────────────────────────────────────────────
  const [brandId,    setBrandId]    = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [modelId,    setModelId]    = useState('');
  const [serviceId,  setServiceId]  = useState('');

  // ── Derived values ────────────────────────────────────────────────────────

  const selectedBrand = BRANDS.find(b => b.id === brandId) ?? null;

  // If brand maps to only 1 category, auto-select it
  const autoCategory: DeviceCategory | null = useMemo(() => {
    if (!selectedBrand) return null;
    const cats = selectedBrand.categories.filter(c =>
      c === 'other' ||
      STABLE_DEVICE_MODELS[c] !== undefined ||
      allServices.some(s => s.deviceCategory === c),
    );
    return cats.length === 1 ? cats[0] : null;
  }, [selectedBrand, allServices]);

  const effectiveCategoryId: DeviceCategory | '' =
    (autoCategory ?? categoryId) as DeviceCategory | '';

  // Category options shown when brand has multiple eligible cats
  const categoryOptions: DeviceCategory[] = useMemo(() => {
    if (!selectedBrand) return [];
    return selectedBrand.categories.filter(c =>
      c === 'other' ||
      STABLE_DEVICE_MODELS[c] !== undefined ||
      allServices.some(s => s.deviceCategory === c),
    );
  }, [selectedBrand, allServices]);

  const needsCategoryPicker = !autoCategory && categoryOptions.length > 1;

  // Model options
  const modelOptions = useMemo(() => {
    if (!effectiveCategoryId) return [];
    const stable = STABLE_DEVICE_MODELS[effectiveCategoryId as DeviceCategory];
    if (stable) return stable;
    // Derive from sheet for non-stable categories (phones, etc.)
    const seen = new Set<string>();
    const models: { id: string; label: string }[] = [];
    allServices
      .filter(s => s.deviceCategory === effectiveCategoryId)
      .forEach(s => s.deviceModels.forEach(m => {
        if (!seen.has(m.toLowerCase())) {
          seen.add(m.toLowerCase());
          models.push({ id: m.toLowerCase(), label: m });
        }
      }));
    return models;
  }, [effectiveCategoryId, allServices]);

  const hasModels = modelOptions.length > 0;

  // Services filtered by category + model
  const serviceOptions = useMemo(() => {
    if (!effectiveCategoryId) return [];
    const modelLower = modelId.toLowerCase();
    return allServices.filter(s => {
      if (s.deviceCategory !== effectiveCategoryId) return false;
      if (!hasModels) return true;
      if (!modelId) return false;
      return (
        s.deviceModels.length === 0 ||
        s.deviceModels.some(m => m.toLowerCase() === modelLower)
      );
    });
  }, [effectiveCategoryId, modelId, allServices, hasModels]);

  const selectedService: ServiceEntry | null =
    serviceOptions.find(s => s.id === serviceId) ?? null;

  // ── Change handlers (cascade resets) ─────────────────────────────────────

  function handleBrand(id: string) {
    setBrandId(id);
    setCategoryId('');
    setModelId('');
    setServiceId('');
  }

  function handleCategory(id: string) {
    setCategoryId(id);
    setModelId('');
    setServiceId('');
  }

  function handleModel(id: string) {
    setModelId(id);
    setServiceId('');
  }

  // ── Schedule handoff ──────────────────────────────────────────────────────

  function handleSchedule() {
    if (!selectedService) return;
    navigate('/book-repair', {
      state: {
        preselectedService: selectedService,
        preselectedModelId: modelId || null,
      },
    });
  }

  // ── Price display ─────────────────────────────────────────────────────────

  const priceStr = selectedService
    ? (selectedService.basePrice !== null ? `$${selectedService.basePrice}` : 'Get a Quote')
    : null;

  // ── Step active states ─────────────────────────────────────────────────────

  const step1Active = true;
  const step2Active = !!brandId && needsCategoryPicker;
  const step3Active = !!effectiveCategoryId && hasModels;
  const step4Active = !!effectiveCategoryId && (!hasModels || !!modelId);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <section id="repair-selector" className="py-14 px-4 border-b border-gray-800">
      <div className="max-w-xl mx-auto">

        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-[#E0E0E0]">What Are You Fixing?</h2>
          <p className="text-[#B0B0B0] text-sm">Select your device to see pricing and book your repair.</p>
        </div>

        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 sm:p-6 space-y-4">

          {/* ── Brand ── */}
          <div>
            <Label active={step1Active}>Brand</Label>
            <div className="relative">
              <select
                value={brandId}
                onChange={e => handleBrand(e.target.value)}
                className={selClass(false)}
              >
                <option value="">Select brand…</option>
                {BRANDS.map(b => (
                  <option key={b.id} value={b.id}>{b.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>
          </div>

          {/* ── Device / Category (only when brand has multiple) ── */}
          {brandId && needsCategoryPicker && (
            <div>
              <Label active={true}>Device</Label>
              <div className="relative">
                <select
                  value={categoryId}
                  onChange={e => handleCategory(e.target.value)}
                  className={selClass(false)}
                >
                  <option value="">Select device…</option>
                  {categoryOptions.map(c => (
                    <option key={c} value={c}>{DEVICE_CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
              </div>
            </div>
          )}

          {/* ── Model ── */}
          {hasModels && (
            <div>
              <Label active={step3Active}>Model</Label>
              <div className="relative">
                <select
                  value={modelId}
                  disabled={!effectiveCategoryId}
                  onChange={e => handleModel(e.target.value)}
                  className={selClass(!effectiveCategoryId)}
                >
                  <option value="">{effectiveCategoryId ? 'Select model…' : '—'}</option>
                  {modelOptions.map(m => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
              </div>
            </div>
          )}

          {/* ── Problem / Service ── */}
          <div>
            <Label active={step4Active}>Problem / Repair</Label>
            <div className="relative">
              <select
                value={serviceId}
                disabled={!step4Active}
                onChange={e => setServiceId(e.target.value)}
                className={selClass(!step4Active)}
              >
                <option value="">{step4Active ? 'Select the issue…' : '—'}</option>
                {serviceOptions.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.repairType}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* ── Service result card ── */}
        {selectedService && (
          <div className="mt-4 bg-gray-900 border border-[#39FF14]/30 rounded-2xl p-5 sm:p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-[#39FF14] uppercase tracking-widest mb-1">
                  {DEVICE_CATEGORY_LABELS[selectedService.deviceCategory]}
                  {modelId && ` — ${modelOptions.find(m => m.id === modelId)?.label ?? modelId}`}
                </p>
                <h3 className="text-lg font-bold text-[#E0E0E0] leading-tight">{selectedService.repairType}</h3>
              </div>
              <div className="text-right shrink-0">
                <p className="text-2xl font-black text-[#39FF14]">{priceStr}</p>
                {selectedService.basePrice !== null && (
                  <p className="text-xs text-gray-500">+ parts if needed</p>
                )}
              </div>
            </div>

            {selectedService.description && (
              <p className="text-sm text-[#B0B0B0] leading-relaxed">{selectedService.description}</p>
            )}

            <div className="flex flex-wrap gap-3 text-xs">
              {selectedService.turnaround && (
                <span className="flex items-center gap-1.5 text-[#B0B0B0]">
                  <Clock className="h-3.5 w-3.5 text-[#39FF14]" />
                  {selectedService.turnaround}
                </span>
              )}
              {selectedService.warranty && (
                <span className="flex items-center gap-1.5 text-[#B0B0B0]">
                  <Shield className="h-3.5 w-3.5 text-[#39FF14]" />
                  {selectedService.warranty}
                </span>
              )}
              {selectedService.disclaimer && (
                <span className="flex items-center gap-1.5 text-[#B0B0B0]">
                  <Wrench className="h-3.5 w-3.5 text-[#39FF14]" />
                  {selectedService.disclaimer}
                </span>
              )}
            </div>

            <button
              onClick={handleSchedule}
              className="w-full bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold px-6 py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              Schedule Repair <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Supporting copy */}
        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          {[
            { label: 'Fast Turnaround',      sub: 'Many repairs same day' },
            { label: 'Upfront Pricing',       sub: 'No surprises' },
            { label: 'SD · Temecula · Mail',  sub: 'Three ways to get fixed' },
          ].map(({ label, sub }) => (
            <div key={label} className="bg-gray-900/60 rounded-xl p-3 border border-gray-800">
              <p className="text-xs font-semibold text-[#E0E0E0]">{label}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
